import { AnalyticsEvent, EventData, ProcessOptions } from './types';
import { DEFAULT_CONFIG } from './constants';
type EventCallback = (eventName: string, eventData: EventData) => void;
export class EventProcessor {
    private eventQueue: AnalyticsEvent[] = [];
    private debounceTimers: Map<string, number> = new Map();
    private throttleTimestamps: Map<string, number> = new Map();
    private batchBuffer: AnalyticsEvent[] = [];
    private flushTimer: number | null = null;
    constructor(private sendCallback: EventCallback) { }
    /**
     * Process a single event with optional debouncing, throttling, and batching
     */
    processEvent(eventName: string, eventData: EventData, options: ProcessOptions = {}): void {
        const { debounceMs, throttleMs, batch = false, priority = 'normal', } = options;
        // Create event object
        const event: AnalyticsEvent = {
            name: eventName,
            data: eventData,
            timestamp: Date.now(),
            sessionId: eventData['sessionId'] || '',
        };
        // Handle debouncing
        if (debounceMs !== undefined && debounceMs > 0) {
            this.debounceEvent(event, debounceMs);
            return;
        }
        // Handle throttling
        if (throttleMs !== undefined && throttleMs > 0) {
            if (this.isThrottled(eventName, throttleMs)) {
                return;
            }
            this.updateThrottleTimestamp(eventName);
        }
        // Handle batching
        if (batch) {
            this.addToBatch(event);
            return;
        }
        // Handle priority
        if (priority === 'high') {
            this.sendImmediately(event);
        }
        else if (priority === 'low') {
            this.addToQueue(event);
        }
        else {
            // Normal priority - send immediately
            this.sendImmediately(event);
        }
    }
    /**
     * Process a batch of events
     */
    processBatch(events: AnalyticsEvent[]): void {
        events.forEach(event => {
            this.addToBatch(event);
        });
        this.flushBatch();
    }
    /**
     * Flush all pending events immediately
     */
    flush(): void {
        // Flush batch buffer
        this.flushBatch();
        // Flush event queue
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift();
            if (event) {
                this.sendCallback(event.name, event.data);
            }
        }
        // Clear all debounce timers
        this.debounceTimers.forEach(timer => window.clearTimeout(timer));
        this.debounceTimers.clear();
    }
    /**
     * Get current queue size
     */
    getQueueSize(): number {
        return this.eventQueue.length;
    }
    /**
     * Get current batch size
     */
    getBatchSize(): number {
        return this.batchBuffer.length;
    }
    /**
     * Clear all pending events and timers
     */
    clear(): void {
        this.eventQueue = [];
        this.batchBuffer = [];
        this.debounceTimers.forEach(timer => window.clearTimeout(timer));
        this.debounceTimers.clear();
        this.throttleTimestamps.clear();
        if (this.flushTimer) {
            window.clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
    }
    /**
     * Debounce an event - delay sending until no more events for the specified time
     */
    private debounceEvent(event: AnalyticsEvent, debounceMs: number): void {
        const key = `${event.name}_${event.data['rowIndex'] || ''}_${event.data['parameterName'] || ''}`;
        // Clear existing timer for this event
        const existingTimer = this.debounceTimers.get(key);
        if (existingTimer) {
            window.clearTimeout(existingTimer);
        }
        // Set new timer
        const timer = window.setTimeout(() => {
            this.sendCallback(event.name, event.data);
            this.debounceTimers.delete(key);
        }, debounceMs);
        this.debounceTimers.set(key, timer);
    }
    /**
     * Check if an event is currently throttled
     */
    private isThrottled(eventName: string, throttleMs: number): boolean {
        const lastTimestamp = this.throttleTimestamps.get(eventName);
        if (!lastTimestamp) {
            return false;
        }
        const timeSinceLastEvent = Date.now() - lastTimestamp;
        return timeSinceLastEvent < throttleMs;
    }
    /**
     * Update throttle timestamp for an event
     */
    private updateThrottleTimestamp(eventName: string): void {
        this.throttleTimestamps.set(eventName, Date.now());
    }
    /**
     * Add event to batch buffer
     */
    private addToBatch(event: AnalyticsEvent): void {
        this.batchBuffer.push(event);
        // Auto-flush if batch reaches size limit
        if (this.batchBuffer.length >= DEFAULT_CONFIG.EVENT_BATCH_SIZE) {
            this.flushBatch();
        }
        else {
            // Schedule auto-flush if not already scheduled
            if (!this.flushTimer) {
                this.flushTimer = window.setTimeout(() => {
                    this.flushBatch();
                }, 1000); // Flush after 1 second of inactivity
            }
        }
    }
    /**
     * Flush batch buffer
     */
    private flushBatch(): void {
        if (this.batchBuffer.length === 0) {
            return;
        }
        // Send all events in batch
        this.batchBuffer.forEach(event => {
            this.sendCallback(event.name, event.data);
        });
        // Clear batch buffer
        this.batchBuffer = [];
        // Clear flush timer
        if (this.flushTimer) {
            window.clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
    }
    /**
     * Add event to queue
     */
    private addToQueue(event: AnalyticsEvent): void {
        // Enforce queue size limit
        if (this.eventQueue.length >= DEFAULT_CONFIG.MAX_QUEUE_SIZE) {
            // Remove oldest event
            this.eventQueue.shift();
        }
        this.eventQueue.push(event);
        // Process queue using requestIdleCallback if available
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(() => this.processQueue());
        }
        else {
            // Fallback to setTimeout
            window.setTimeout(() => this.processQueue(), 0);
        }
    }
    /**
     * Process events from queue
     */
    private processQueue(): void {
        // Process up to 5 events at a time
        const batchSize = Math.min(5, this.eventQueue.length);
        for (let i = 0; i < batchSize; i++) {
            const event = this.eventQueue.shift();
            if (event) {
                this.sendCallback(event.name, event.data);
            }
        }
        // Schedule next batch if queue not empty
        if (this.eventQueue.length > 0) {
            if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(() => this.processQueue());
            }
            else {
                window.setTimeout(() => this.processQueue(), 100);
            }
        }
    }
    /**
     * Send event immediately
     */
    private sendImmediately(event: AnalyticsEvent): void {
        this.sendCallback(event.name, event.data);
    }
}
